import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { ButtonLink, Icon, IconButton } from "@wato/ui";
import { useApi } from "../../api/context";
import type { AnalyticsConversationsView, AnalyticsOverviewView } from "../../api/analytics-types";
import type { ChannelSummary, WalletInfo } from "../../api/types";
import { formatBasisPoints } from "../../pages/analytics/overview";
import { formatAmount } from "../billing/format";
import { isoDayInZone } from "../../lib/analytics-range";
import { mayUse } from "../roles/role-label";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { loadHomeSummary } from "./home-summary";

interface Props {
  readonly workspaceId: string;
}

type Part<T> = { readonly ok: true; readonly value: T } | { readonly ok: false };
const settle = async <T,>(p: Promise<T>): Promise<Part<T>> =>
  p.then(
    (value) => ({ ok: true as const, value }),
    () => ({ ok: false as const }),
  );

interface KpiData {
  readonly overview: Part<AnalyticsOverviewView>;
  readonly conversations: Part<AnalyticsConversationsView>;
  readonly unassigned: Part<number>;
  readonly wallet: Part<WalletInfo>;
  readonly channels: Part<ChannelSummary[]>;
}

/** Conversation-weighted mean of the per-channel average first-response times (ms), or null. */
/** Unassigned is a subset of open: the rollup "open" can lag the live unassigned queue, so never show unassigned > open. */
export function openUnassignedCounts(
  open: number,
  unassigned: number,
): { open: number; unassigned: number } {
  return { open: Math.max(open, unassigned), unassigned };
}

export function weightedFirstResponseMs(view: AnalyticsConversationsView): number | null {
  let sum = 0;
  let weight = 0;
  for (const c of view.byChannel) {
    if (c.avgFirstResponseMs === null) continue;
    const w = Math.max(c.conversationsNew, 1);
    sum += c.avgFirstResponseMs * w;
    weight += w;
  }
  return weight === 0 ? null : sum / weight;
}

/** IDEA-02: the home dashboard KPI tiles. Every tile loads independently and degrades to a dash. */
export function HomeKpis({ workspaceId }: Props) {
  const { t, locale } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  // Q4-ONB-08: a member without analytics.view never fires the (403) analytics calls.
  const canViewAnalytics = mayUse(workspace, "analytics.view");
  const [data, setData] = useState<KpiData | null>(null);
  const [attempt, setAttempt] = useState(0);
  const day = useMemo(() => isoDayInZone(new Date()), []);
  const num = useMemo(
    () => new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en"),
    [locale],
  );

  useEffect(() => {
    let live = true;
    const range = { from: day, to: day };
    void Promise.all([
      canViewAnalytics
        ? settle(api.getAnalyticsOverview(workspaceId, range))
        : Promise.resolve<Part<AnalyticsOverviewView>>({ ok: false }),
      canViewAnalytics
        ? settle(api.getAnalyticsConversations(workspaceId, range))
        : Promise.resolve<Part<AnalyticsConversationsView>>({ ok: false }),
      settle(loadHomeSummary(api, workspaceId).then((s) => s.unassigned)),
      settle(api.getWallet(workspaceId)),
      settle(api.listChannels(workspaceId)),
    ]).then(([overview, conversations, unassigned, wallet, channels]) => {
      if (live) setData({ overview, conversations, unassigned, wallet, channels });
    });
    return () => {
      live = false;
    };
  }, [api, workspaceId, day, attempt, canViewAnalytics]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const dash = "—";
  const channelsEmpty = data?.channels.ok === true && data.channels.value.length === 0;

  const tiles: { key: string; label: string; value: string; note?: string; help?: string }[] = [];
  if (data) {
    const o = data.overview.ok ? data.overview.value : null;
    const frt = data.conversations.ok ? weightedFirstResponseMs(data.conversations.value) : null;
    const channels = data.channels.ok ? data.channels.value : null;
    tiles.push(
      {
        key: "conversationsToday",
        label: t("dashboard.home.kpi.conversationsToday"),
        help: t("analytics.kpiHelp.conversationsNew"),
        value: o ? t("dashboard.home.kpi.conversationsUnit", { count: o.conversations.new }) : dash,
      },
      {
        key: "firstResponse",
        label: t("dashboard.home.kpi.firstResponse"),
        help: t("analytics.kpiHelp.firstResponse"),
        value:
          frt === null
            ? dash
            : t("dashboard.home.kpi.minutes", { value: num.format(Math.round(frt / 60_000)) }),
      },
      {
        key: "openUnassigned",
        label: t("dashboard.home.kpi.openUnassigned"),
        value:
          o && data.unassigned.ok
            ? t("dashboard.home.kpi.openOnlyValue", {
                open: num.format(
                  openUnassignedCounts(o.conversations.open, data.unassigned.value).open,
                ),
              })
            : dash,
        ...(o && data.unassigned.ok
          ? {
              note: t("dashboard.home.kpi.unassignedNote", {
                unassigned: num.format(data.unassigned.value),
              }),
            }
          : {}),
      },
      {
        key: "aiResolution",
        label: t("dashboard.home.kpi.aiResolution"),
        help: t("analytics.kpiHelp.aiResolution"),
        value: o ? formatBasisPoints(o.automation.rateBasisPoints) : dash,
      },
      {
        key: "revenue",
        label: t("dashboard.home.kpi.revenue"),
        value:
          o && o.money.length > 0
            ? o.money.map((m) => formatAmount(m.revenueMinor, m.currency, locale)).join(" · ")
            : dash,
      },
      {
        key: "recoveredCarts",
        label: t("dashboard.home.kpi.recoveredCarts"),
        value: dash,
        note: t("dashboard.home.kpi.recoveredCartsNote"),
      },
      {
        key: "wallet",
        label: t("dashboard.home.kpi.wallet"),
        value: data.wallet.ok
          ? formatAmount(data.wallet.value.balanceMinor, data.wallet.value.currency, locale)
          : dash,
      },
      {
        key: "channelHealth",
        label: t("dashboard.home.kpi.channelHealth"),
        value: !channels
          ? dash
          : channels.length === 0
            ? t("dashboard.home.kpi.noChannels")
            : t("dashboard.home.kpi.channelsOk", {
                ok: channels.filter((c) => c.status === "connected").length,
                total: channels.length,
              }),
      },
    );
  }
  const anyFailed = data ? Object.values(data).some((p) => !(p as Part<unknown>).ok) : false;

  return (
    <section aria-label={t("dashboard.home.kpi.title")} className="flex flex-col gap-3">
      <h3 className="text-base font-semibold text-fg">{t("dashboard.home.kpi.title")}</h3>
      {data ? null : (
        <p role="status" aria-busy="true" className="text-sm text-fg-muted">
          {t("dashboard.shell.loading")}
        </p>
      )}
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            title={[tile.help, tile.note].filter(Boolean).join(" · ") || undefined}
            className="flex flex-col gap-1 rounded-2xl border border-app-line bg-app-surface p-5"
          >
            <p className="text-xs text-app-muted">{tile.label}</p>
            <p
              data-testid={`home-kpi-${tile.key}`}
              className="text-3xl font-bold text-app-fg tabular-nums"
            >
              {tile.value}
            </p>
            {tile.note ? <p className="sr-only">{tile.note}</p> : null}
            {tile.key === "openUnassigned" ? (
              <ButtonLink
                as={Link}
                to={`/w/${workspaceId}/conversations`}
                variant="secondary"
                size="sm"
                className="mt-2"
              >
                {t("dashboard.home.kpi.openInbox")}
              </ButtonLink>
            ) : null}
            {tile.key === "channelHealth" && channelsEmpty ? (
              <>
                <p className="sr-only">{t("dashboard.home.kpi.emptyGuidance")}</p>
                <ButtonLink
                  as={Link}
                  to={`/w/${workspaceId}/channels`}
                  size="sm"
                  className="mt-2"
                  title={t("dashboard.home.kpi.emptyGuidance")}
                >
                  {t("analytics.empty.ctaConnect")}
                </ButtonLink>
              </>
            ) : null}
          </div>
        ))}
      </div>
      {anyFailed ? (
        <p className="text-sm text-fg-muted">
          {t("dashboard.home.kpi.unavailable")}{" "}
          <IconButton
            label={t("dashboard.home.kpi.retry")}
            icon={<Icon name="refresh" />}
            variant="ghost"
            onClick={retry}
          />
        </p>
      ) : null}
    </section>
  );
}
